import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Gift, Rocket, Copy, LogOut, Ticket, Coins, Percent, ImageDown, Share2, BarChart3, ArrowRight } from "lucide-react";
import { money } from "@/data/products";
import AmbassadorAssets from "@/components/AmbassadorAssets";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { paid: "Paid", shipped: "Shipped", delivered: "Delivered", pending: "Pending", cancelled: "Cancelled" };

const BENEFITS = [
  { icon: Ticket, title: "Your own referral code", desc: "A unique code plus a shareable link, issued the moment you're approved." },
  { icon: Coins, title: "Earn on every order", desc: "A competitive commission (typically 15%) on each paid order from your referrals." },
  { icon: Percent, title: "A discount for your audience", desc: "Your followers save (typically 10%) automatically at checkout with your code." },
  { icon: ImageDown, title: "Ready-made promo kit", desc: "Download branded graphics — post, story & banner — with your code baked in, plus copy-paste captions." },
  { icon: Share2, title: "One-tap sharing", desc: "A link that auto-applies your discount, so your audience just clicks and checks out." },
  { icon: BarChart3, title: "Live earnings dashboard", desc: "Track clicks, referrals, conversion and payouts in real time." },
];

const HOW_IT_WORKS = [
  { title: "Apply", desc: "Tell us about you and your audience. It takes about two minutes." },
  { title: "Get approved", desc: "We review every applicant personally and email your code + dashboard access." },
  { title: "Share", desc: "Post your branded graphics and drop your personal link." },
  { title: "Earn", desc: "Commission is credited on every paid order, and we pay out your balance." },
];

const PROGRAM_FAQ = [
  {
    q: "When do I actually earn commission?",
    a: "Commission is credited the moment a referred order is paid, and it stays credited through shipping and delivery. Orders that are cancelled or refunded before payment never count — so your balance always reflects real, completed sales.",
  },
  {
    q: "How and when do I get paid out?",
    a: "You're paid every two weeks by PayPal or Molecularix store credit — your choice. Each cycle we pay out balances of $50 or more; anything under $50 simply rolls over to the next payout. Only commission from paid orders that have cleared our refund window is included. Add a valid PayPal email in your dashboard (or opt for store credit) — payouts are in USD.",
  },
  {
    q: "What discount does my code give, and what do I earn?",
    a: "Your unique code automatically applies a set discount for your audience (typically 10%) at checkout. Your own commission rate (typically 15% of each referred order) is shown at the top of your dashboard.",
  },
  {
    q: "Where can I share my link?",
    a: "Anywhere your audience is — social posts, link-in-bio, stories, DMs, or email. Your link auto-applies your discount, so your audience just clicks and checks out. You can also download ready-made graphics with your code baked in.",
  },
  {
    q: "What are the promotion rules? (Research Use Only)",
    a: "Every Molecularix product is strictly for research use only. Your promotions must never claim or imply human or animal use, dosing, or health/therapeutic benefits. Keep messaging professional and compliant — codes that break these rules may be paused.",
  },
];

export default function Ambassador() {
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("view") === "login" ? "dashboard" : "apply"
  ); // apply | dashboard
  const [form, setForm] = useState({ name: "", email: "", social: "", audience: "", why: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [loginEmail, setLoginEmail] = useState(localStorage.getItem("mx_amb_email") || "");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState("paypal");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [payoutDetails, setPayoutDetails] = useState("");
  const [savingPayout, setSavingPayout] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const apply = async () => {
    if (!form.name.trim()) { toast.error("Enter your name"); return; }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ambassadors/apply`, { ...form, social: form.platform ? `${form.platform}: ${form.social}` : form.social, email: form.email.trim().toLowerCase() });
      setSubmitted(true);
      toast.success("Application received — we'll be in touch soon.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit application"); }
    finally { setBusy(false); }
  };

  const loadMe = async (email) => {
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { toast.error("Enter a valid email"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ambassadors/me`, { email: email.trim().toLowerCase() });
      setMe(data);
      setPayMethod(data.payout_method || "paypal");
      setPaypalEmail(data.paypal_email || "");
      setPayoutDetails(data.payout_details || "");
      localStorage.setItem("mx_amb_email", email.trim().toLowerCase());
    } catch (e) { toast.error(e.response?.data?.detail || "No application found for that email"); setMe(null); }
    finally { setLoading(false); }
  };

  const logoutAmb = () => { localStorage.removeItem("mx_amb_email"); setMe(null); setLoginEmail(""); };
  const safeCopy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy blocked by your browser — long-press / select to copy manually"); }
  };
  const copyCode = () => safeCopy(me.code, "Code");

  const requestOtp = async () => {
    try {
      await axios.post(`${API}/ambassadors/payout-otp`, { email: me.email });
      setOtpSent(true);
      toast.success("Verification code sent to your email");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
  };

  const savePayout = async () => {
    if (payMethod === "paypal" && !/^[^@]+@[^@]+\.[^@]+$/.test(paypalEmail)) { toast.error("Enter a valid PayPal email"); return; }
    if ((payMethod === "crypto" || payMethod === "bank") && !payoutDetails.trim()) { toast.error(payMethod === "crypto" ? "Enter your USDT/USDC address" : "Enter your bank payout details"); return; }
    if (!otpSent) { await requestOtp(); return; }
    if (!otpCode.trim()) { toast.error("Enter the 6-digit code from your email"); return; }
    setSavingPayout(true);
    try {
      const paypal = payMethod === "paypal" ? paypalEmail.trim().toLowerCase() : null;
      await axios.post(`${API}/ambassadors/payout-method`, { email: me.email, method: payMethod, paypal_email: paypal, payout_details: payoutDetails.trim() || null, code: otpCode.trim() });
      setMe((m) => ({ ...m, payout_method: payMethod, paypal_email: paypal || "", payout_details: payoutDetails.trim() }));
      setOtpSent(false); setOtpCode("");
      toast.success("Payout method saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save payout method"); }
    finally { setSavingPayout(false); }
  };

  return (
    <main className="pt-32 pb-28 min-h-screen" data-testid="ambassador-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-5">Molecularix Elite</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] mb-6">Represent the brand.<br />Build your audience.<br /><span className="text-[#D4AF37]">Earn as you grow.</span></h1>
          <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed max-w-2xl">
            Molecularix Elite is our ambassador &amp; referral program for approved creators, communities, and educators.
            Get a personal referral code, a discount for your audience, promotional resources, and commission on eligible paid referrals.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button onClick={() => { setTab("apply"); setTimeout(() => document.querySelector('[data-testid="amb-apply-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }} data-testid="amb-hero-apply"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
              Apply to Become an Ambassador <ArrowRight size={14} />
            </button>
            <button onClick={() => setTab("dashboard")} data-testid="amb-hero-signin"
              className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#E8E8E3] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              Ambassador Login
            </button>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-zinc-800">
          {[["apply", "Apply Now"], ["dashboard", "Ambassador Login"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`amb-tab-${id}`}
              className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${tab === id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "apply" && !submitted && <ProgramDetails />}

        {tab === "apply" && (
          submitted ? (
            <div className="border border-emerald-400/30 bg-emerald-400/[0.05] p-10 text-center" data-testid="amb-applied">
              <Sparkles size={28} className="text-[#D4AF37] mx-auto mb-4" />
              <h2 className="font-serif text-3xl font-light mb-3">Application received</h2>
              <p className="text-[#A1A1AA] text-sm">We review every applicant personally. If approved, you'll get an email with your personal referral code and dashboard access.</p>
            </div>
          ) : (
            <div className="border border-zinc-800 bg-[#0C0C0C] p-6 md:p-8" data-testid="amb-apply-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" testid="amb-name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
                <Field label="Email" testid="amb-email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} type="email" />
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-1.5">Primary platform</label>
                  <select value={form.platform || ""} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} data-testid="amb-platform"
                    className="w-full bg-[#0C0C0C] border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37]">
                    <option value="">Select…</option><option>Instagram</option><option>X (Twitter)</option><option>Telegram</option><option>TikTok</option><option>YouTube</option><option>Other</option>
                  </select>
                </div>
                <Field label="Handle / username" testid="amb-social" value={form.social} onChange={(v) => setForm((f) => ({ ...f, social: v }))} />
                <Field label="Estimated audience size" testid="amb-audience" value={form.audience} onChange={(v) => setForm((f) => ({ ...f, audience: v }))} />
              </div>
              <div className="mt-4">
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-1.5">Why do you want to represent Molecularix?</label>
                <textarea value={form.why} onChange={(e) => setForm((f) => ({ ...f, why: e.target.value }))} rows={3} data-testid="amb-why"
                  className="w-full bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] resize-none" />
              </div>
              <button onClick={apply} disabled={busy} data-testid="amb-apply-btn"
                className="mt-6 bg-[#D4AF37] text-black px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40">
                {busy ? "Submitting…" : "Submit Application"}
              </button>
              <p className="mt-4 font-mono text-[10px] text-[#52525B]">For research-use-only products. Ambassadors must comply with our RUO messaging standards.</p>
            </div>
          )
        )}

        {tab === "dashboard" && (
          !me ? (
            <div className="border border-zinc-800 bg-[#0C0C0C] p-8 max-w-md" data-testid="amb-login">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-2">Ambassador login</p>
              <p className="text-[#A1A1AA] text-sm mb-5">Enter the email you applied with to view your code and earnings.</p>
              <input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} type="email" placeholder="your@email.com" data-testid="amb-login-input"
                onKeyDown={(e) => e.key === "Enter" && loadMe(loginEmail)}
                className="w-full bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 font-mono text-sm placeholder:text-[#52525B]" />
              <button onClick={() => loadMe(loginEmail)} disabled={loading} data-testid="amb-login-btn"
                className="mt-6 w-full bg-[#D4AF37] text-black py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40">
                {loading ? "Loading…" : "View Dashboard"}
              </button>
            </div>
          ) : (
            <div data-testid="amb-dashboard">
              <div className="flex items-center justify-between gap-4 mb-6">
                <p className="font-mono text-sm text-[#A1A1AA]" data-testid="amb-me-status">Signed in as {me.email} · <span className="text-[#D4AF37] uppercase">{me.status}</span></p>
                <button onClick={logoutAmb} data-testid="amb-logout-btn" className="flex items-center gap-2 border border-zinc-700 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-red-400 hover:border-red-400/50 transition-colors"><LogOut size={13} /> Log out</button>
              </div>

              {me.status !== "approved" ? (
                <div className="border border-zinc-800 bg-[#0C0C0C] p-8 text-center" data-testid="amb-status-note">
                  <p className="text-[#A1A1AA]">{me.status === "pending" ? "Your application is under review. We'll email you as soon as it's approved." : "This application was not approved."}</p>
                </div>
              ) : (
                <>
                  <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="amb-code-card">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-1">Your referral code · {me.buyer_discount}% off for them</p>
                      <p className="font-serif text-4xl text-[#D4AF37]" data-testid="amb-code">{me.code}</p>
                    </div>
                    <button onClick={copyCode} data-testid="amb-copy-code" className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-black transition-colors"><Copy size={13} /> Copy</button>
                  </div>

                  {me.tier && (
                    <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.04] p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="amb-tier-card">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Tier {me.tier} · {me.tier_name}</p>
                        <p className="font-mono text-xs text-[#A1A1AA]">You earn <span className="text-[#D4AF37]">{me.commission_rate}%</span> · your code gives <span className="text-[#D4AF37]">{me.buyer_discount}% off</span></p>
                      </div>
                      {me.tier_progress && (
                        <div className="min-w-[200px] flex-1 max-w-xs" data-testid="amb-tier-progress">
                          <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#52525B] mb-1">Next: {me.tier_progress.next}</p>
                          {me.tier_progress.current != null && me.tier_progress.target ? (
                            <>
                              <div className="h-1.5 bg-zinc-900"><div className="h-1.5 bg-[#D4AF37]" style={{ width: `${Math.min(100, (me.tier_progress.current / me.tier_progress.target) * 100)}%` }} /></div>
                              <p className="font-mono text-[10px] text-[#A1A1AA] mt-1 tabular-nums">${me.tier_progress.current.toLocaleString()} / ${me.tier_progress.target.toLocaleString()} · {me.tier_progress.rule}</p>
                            </>
                          ) : (
                            <p className="font-mono text-[10px] text-[#A1A1AA]">{me.tier_progress.rule}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border border-zinc-800 bg-[#0C0C0C] p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="amb-link-card">
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-1">Your referral link · auto-applies your discount</p>
                      <p className="font-mono text-xs sm:text-sm text-[#E8E8E3] truncate" data-testid="amb-link">{`${window.location.origin}/?ref=${me.code}`}</p>
                    </div>
                    <button onClick={() => safeCopy(`${window.location.origin}/?ref=${me.code}`, "Link")} data-testid="amb-copy-ref-link"
                      className="shrink-0 flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors"><Copy size={13} /> Copy Link</button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                    <Stat label="Link clicks" value={me.clicks || 0} testid="amb-stat-clicks" />
                    <Stat label="Referrals" value={me.referrals} testid="amb-stat-referrals" />
                    <Stat label="Click → Sale" value={me.clicks ? `${Math.round((me.referrals / me.clicks) * 100)}%` : "—"} testid="amb-stat-conversion" gold />
                    <Stat label="Referred sales" value={money(me.sales)} testid="amb-stat-sales" />
                    <Stat label={`Earned (${me.commission_rate}%)`} value={money(me.earned)} testid="amb-stat-earned" gold />
                    <Stat label="Unpaid" value={money(me.unpaid)} testid="amb-stat-unpaid" gold />
                  </div>

                  <div className="border border-zinc-800 bg-[#0C0C0C] p-5 mb-8" data-testid="amb-payout-settings">
                    <div className="flex items-center gap-2 mb-2">
                      <Coins size={15} className="text-[#D4AF37]" />
                      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E8E8E3]">Payout method</p>
                    </div>
                    <p className="text-[#71717A] text-xs mb-4 leading-relaxed">Paid every two weeks, once your unpaid balance reaches $50 (smaller balances roll over). Choose how you'd like to receive your commission.</p>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {[["paypal", "PayPal"], ["crypto", "Crypto (USDT/USDC)"], ["bank", "Bank payout"], ["store_credit", "Store credit"]].map(([m, lbl]) => (
                        <button key={m} onClick={() => setPayMethod(m)} data-testid={`amb-payout-opt-${m}`}
                          className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${payMethod === m ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/[0.06]" : "border-zinc-700 text-[#A1A1AA] hover:border-zinc-500"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    {payMethod === "paypal" ? (
                      <input value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} type="email" placeholder="your-paypal@email.com" data-testid="amb-paypal-input"
                        className="w-full max-w-md bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] mb-4" />
                    ) : payMethod === "crypto" || payMethod === "bank" ? (
                      <input value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} data-testid="amb-payout-details-input"
                        placeholder={payMethod === "crypto" ? "USDT/USDC wallet address + network (e.g. TRC-20)" : "Bank name, account holder, routing + account no."}
                        className="w-full max-w-md bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] mb-4" />
                    ) : (
                      <p className="text-[#71717A] text-xs mb-4">We'll issue your commission as Molecularix store credit toward your own orders.</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      {otpSent && (
                        <input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="6-digit email code" maxLength={6} data-testid="amb-otp-input"
                          className="bg-transparent border border-[#D4AF37]/60 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37] w-40 tracking-[3px]" />
                      )}
                      <button onClick={savePayout} disabled={savingPayout} data-testid="amb-payout-save"
                        className="bg-[#D4AF37] text-black px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors disabled:opacity-40">
                        {savingPayout ? "Saving…" : otpSent ? "Confirm with code" : "Save payout method"}
                      </button>
                      {otpSent && <button onClick={requestOtp} className="font-mono text-[10px] text-[#71717A] hover:text-[#D4AF37] underline" data-testid="amb-otp-resend">Resend code</button>}
                      <span className="font-mono text-[10px] text-[#52525B]" data-testid="amb-payout-current">
                        Current: {me.payout_method === "store_credit" ? "Store credit"
                          : me.payout_method === "crypto" ? `Crypto · ${me.payout_details || "no details"}`
                          : me.payout_method === "bank" ? `Bank · ${me.payout_details || "no details"}`
                          : `PayPal · ${me.paypal_email || "no email set"}`}
                      </span>
                    </div>
                    {otpSent && <p className="font-mono text-[10px] text-[#D4AF37]/70 mt-2" data-testid="amb-otp-note">For your security we emailed a one-time code to {me.email}. Enter it above to confirm the change.</p>}
                  </div>

                  <AmbassadorAssets code={me.code} discount={me.buyer_discount} />

                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1AA] mb-4 mt-10">Recent referred orders</h3>
                  {me.recent_orders.length === 0 ? (
                    <p className="font-mono text-sm text-[#52525B] py-8" data-testid="amb-no-orders">No referred orders yet — share your code to start earning.</p>
                  ) : (
                    <div className="border border-zinc-800">
                      {me.recent_orders.map((o) => (
                        <div key={o.order_ref} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-900 text-sm" data-testid={`amb-order-${o.order_ref}`}>
                          <span className="font-mono text-[#E8E8E3]">{o.order_ref}</span>
                          <span className="font-mono text-[11px] text-[#71717A]">{STATUS_LABEL[o.status] || o.status}</span>
                          <span className="font-mono text-[#A1A1AA]">{money(o.total)}</span>
                          <span className={`font-mono ${o.commission_paid ? "text-emerald-400" : o.qualifies ? "text-[#D4AF37]" : "text-[#52525B]"}`}>
                            {money(o.commission_amount)}{o.commission_paid ? " ✓" : o.qualifies ? "" : " (pending)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        )}
      </div>
    </main>
  );
}

const ProgramDetails = () => (
  <section className="mb-12" data-testid="amb-program-details">
    {/* Who it's for */}
    <div className="border border-zinc-800 bg-[#0C0C0C] p-6 mb-14 grid sm:grid-cols-2 gap-6" data-testid="amb-who-for">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">What is it?</p>
        <p className="text-[#A1A1AA] text-sm leading-relaxed">Molecularix's official ambassador &amp; referral program — apply, get approved, share your code, earn on eligible paid referrals.</p>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Who is it for?</p>
        <p className="text-[#A1A1AA] text-sm leading-relaxed">Approved creators, communities, educators, and researchers with an audience that fits our strict Research-Use-Only positioning.</p>
      </div>
    </div>

    {/* Commission tiers */}
    <h2 className="font-serif text-3xl font-light mb-6">Commission tiers</h2>
    <div className="grid sm:grid-cols-2 gap-4 mb-6" data-testid="amb-tiers">
      {[
        { n: 1, name: "Associate Researcher", comm: 10, disc: 5, qual: "Default entry level for all approved applicants upon sign-up.", perks: null, gold: false },
        { n: 2, name: "VIP Researcher", comm: 15, disc: 10, qual: "$1,500+ referred sales OR 5,000+ social followers.", perks: "$100/mo store testing credit · priority payouts", gold: false },
        { n: 3, name: "Elite Partner", comm: 25, disc: 15, qual: "$5,000+/mo referred sales OR 25,000+ engaged followers.", perks: "Co-branded portal link · free monthly product supply", gold: true },
        { n: 4, name: "Sovereign Brand Ambassador", comm: 35, disc: 20, qual: "$15,000+/mo referred sales OR 100,000+ audience reach.", perks: "Premier partner package · direct revenue-share split options · instant payouts", gold: true },
      ].map((t) => (
        <div key={t.n} className={`border p-6 ${t.gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]" : "border-zinc-800 bg-[#0C0C0C]"}`} data-testid={`amb-tier-${t.n}`}>
          <p className={`font-mono text-[10px] uppercase tracking-[0.25em] mb-2 ${t.gold ? "text-[#D4AF37]" : "text-[#A1A1AA]"}`}>Tier {t.n} · {t.name}</p>
          <div className="flex items-baseline gap-3 mb-3">
            <p className={`font-mono font-semibold text-5xl tabular-nums ${t.gold ? "text-[#D4AF37]" : "text-[#F4F4F5]"}`}>{t.comm}%</p>
            <p className="font-mono text-[11px] text-[#71717A]">commission · {t.disc}% OFF code for your audience</p>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">{t.qual}</p>
          {t.perks && <p className="font-mono text-[11px] text-[#D4AF37]/80 mt-2">{t.perks}</p>}
        </div>
      ))}
    </div>
    <p className="font-mono text-[10px] text-[#52525B] mb-6" data-testid="amb-tier-note">Tiers 1→3 upgrade automatically as your referred sales qualify. Tier 4 and follower-based promotions are confirmed by our team.</p>
    <div className="border border-zinc-800 bg-[#0C0C0C] p-5 mb-14 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] text-[#A1A1AA]" data-testid="amb-payout-perks">
      <span>· Bi-weekly payouts</span><span>· Crypto (USDT / USDC)</span><span>· Direct bank payout</span><span>· PayPal</span><span>· Store credit</span>
    </div>

    {/* How it works */}
    <h2 className="font-serif text-3xl font-light mb-6">How Molecularix Elite works</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-14">
      {HOW_IT_WORKS.map((s, i) => (
        <motion.div
          key={s.title}
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.08 }}
          className="bg-[#0C0C0C] p-6" data-testid={`amb-how-${i + 1}`}>
          <span className="font-serif text-4xl text-[#D4AF37]/40 leading-none">{String(i + 1).padStart(2, "0")}</span>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E8E8E3] mt-4 mb-2">{s.title}</p>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">{s.desc}</p>
        </motion.div>
      ))}
    </div>

    {/* Benefits */}
    <h2 className="font-serif text-3xl font-light mb-6">What you get</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
      {BENEFITS.map((b, i) => (
        <motion.div
          key={b.title}
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.06 }}
          className="border border-zinc-800 bg-[#0C0C0C] p-6 hover:border-[#D4AF37]/40 transition-colors" data-testid={`amb-benefit-${i + 1}`}>
          <b.icon size={20} className="text-[#D4AF37] mb-4" />
          <p className="font-serif text-xl font-light mb-2">{b.title}</p>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">{b.desc}</p>
        </motion.div>
      ))}
    </div>

    {/* Promotion rules */}
    <h2 className="font-serif text-3xl font-light mb-6">Promotion rules</h2>
    <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-6 mb-14" data-testid="amb-rules">
      <ul className="space-y-2.5 text-sm text-[#A1A1AA] leading-relaxed list-disc list-inside marker:text-[#D4AF37]">
        <li>Every Molecularix product is strictly <span className="text-[#E8E8E3]">Research Use Only</span> — promotions must never claim or imply human or animal use, dosing, or health/therapeutic benefits.</li>
        <li>Share your link anywhere your audience is — social posts, link-in-bio, stories, DMs, or email — using the approved promotional kit.</li>
        <li>Self-referrals do not earn commission, and referral abuse or fraud leads to code suspension.</li>
        <li>Codes that break these rules may be paused or terminated.</li>
      </ul>
    </div>

    {/* FAQ */}
    <h2 className="font-serif text-3xl font-light mb-6">Good to know</h2>
    <Accordion type="single" collapsible className="border border-zinc-800 bg-[#0C0C0C] px-6 mb-14" data-testid="amb-faq">
      {PROGRAM_FAQ.map((f, i) => (
        <AccordionItem key={f.q} value={`faq-${i}`} className="border-zinc-800" data-testid={`amb-faq-${i + 1}`}>
          <AccordionTrigger className="font-mono text-[13px] uppercase tracking-[0.08em] text-[#E8E8E3] hover:no-underline hover:text-[#D4AF37] [&>svg]:text-[#D4AF37]">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-[#A1A1AA] text-sm leading-relaxed max-w-2xl">
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>

    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-4" data-testid="amb-ready-cta">Ready to join Molecularix Elite? Apply below.</p>
  </section>
);


const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-1.5">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testid}
      className="w-full bg-transparent border border-zinc-700 px-3 py-2.5 font-mono text-xs outline-none focus:border-[#D4AF37]" />
  </div>
);

const Stat = ({ label, value, testid, gold }) => (
  <div className="border border-zinc-800 bg-[#0C0C0C] p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52525B] mb-2">{label}</p>
    <p className={`font-serif text-3xl ${gold ? "text-[#D4AF37]" : "text-[#F5F5F0]"}`}>{value}</p>
  </div>
);
